import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import api from "../../api/api"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

export const PcnPaymentsDataFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialIssueDateFrom,
    initialIssueDateTo,
    initialPaymentDateFrom,
    initialPaymentDateTo,
    initialDateSearchMode,
    initialClientId,
    initialSiteId,
    initialSitesByClient,
    initialSelectedTags = [],
    initialAppliedFilters,
    clients = [],
    sites = [],
    tags = [],
    organisationId,
    token
}) => {
    const [issueDateFrom, setIssueDateFrom] = useState(initialIssueDateFrom)
    const [issueDateTo, setIssueDateTo] = useState(initialIssueDateTo)
    const [paymentDateFrom, setPaymentDateFrom] = useState(initialPaymentDateFrom)
    const [paymentDateTo, setPaymentDateTo] = useState(initialPaymentDateTo)
    const [dateSearchMode, setDateSearchMode] = useState(initialDateSearchMode)
    const [clientId, setClientId] = useState(initialClientId)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [sitesByClient, setSitesByClient] = useState(initialSitesByClient)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const clearLocalFilters = () => {
        setIssueDateFrom(null)
        setIssueDateTo(null)
        setPaymentDateFrom(null)
        setPaymentDateTo(null)
        setDateSearchMode(1)
        setClientId("")
        setSiteId("")
        setSitesByClient(sites)
        setSelectedTags([])
        setAppliedFilters({
            "Date Mode": "Allow Overlap"
        })
    }

    const renderDateRangePicker = (label, labelPrefix, fromDate, toDate, setFromDate, setToDate) =>
        <div className="mb-2">
            <label className="m-0 mr-2 mb-1 nowrap">{ label }</label>
            <DateRangePicker
                fromDate={ fromDate }
                toDate={ toDate }
                setDateRange={ (firstDate, lastDate, presetLabel) => {
                    setFromDate(firstDate === null || firstDate === "" ? null : firstDate);
                    setToDate(lastDate === null || lastDate === "" ? null : lastDate);
                    setAppliedFilters(prevState => appliedFiltersUtilities.applyDateRange(prevState, labelPrefix, firstDate, lastDate, presetLabel))
                } }
                showClearOption
                hideLabel
                fullWidth
                dropdownPositionRight
            />
        </div>

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ issueDateFrom, issueDateTo, paymentDateFrom, paymentDateTo, dateSearchMode, clientId, siteId, sitesByClient, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="pcn-payments-data-filters-modal"
            handleReset={ clearLocalFilters }
        >
            <div className="w-100">
                { renderDateRangePicker("Issue Date Range", "Issue Date", issueDateFrom, issueDateTo, setIssueDateFrom, setIssueDateTo) }

                { renderDateRangePicker("Payment Date Range", "Payment Date", paymentDateFrom, paymentDateTo, setPaymentDateFrom, setPaymentDateTo) }

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Mode</label>
                    <select
                        id="date-search-mode-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ dateSearchMode === null ? "" : dateSearchMode }
                        onChange={ e => {
                            const value = Number(e.target.value)
                            setDateSearchMode(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Date Mode": value === 0 ? "Strict" : "Allow Overlap"
                            }))
                        } }
                    >
                        <option value="0">Strict</option>
                        <option value="1">Allow Overlap</option>
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Client</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setClientId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setSitesByClient(sites)
                                setSiteId("")
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Client"]
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                api.getClientSites(
                                    data => {
                                        setSitesByClient(data)
                                        setSiteId("")
                                    },
                                    error => console.log(error),
                                    value,
                                    organisationId,
                                    token
                                )
                                const selectedClient = clients.find(item => item.id === value)
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState, "Client": selectedClient?.name }
                                    delete updatedFilters["Site"]
                                    return updatedFilters
                                })
                            }
                        } }
                        selected={ clientId }
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...sitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSiteId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedSite = sitesByClient.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite?.name }))
                            }
                        } }
                        selected={ siteId }
                    />
                </div>

                { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                        <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                    </div>
                    <TagSelector
                        onChange={ handleTagsChange }
                        selectedTags={ selectedTags }
                        tags={ tags }
                    />
                </div> }
            </div>
        </SearchFiltersModal>
    )
}
